import { Button } from "@/src/components/ui/button";
import { Combobox } from "@/src/components/ui/combobox";
import { Label } from "@/src/components/ui/label";
import { useFormContext } from "react-hook-form";
import { extractFromMultiName } from "../utils";
import { XIcon } from "lucide-react";
import { useHoverState } from "@/src/lib/useHoverState";
import { cn } from "@/src/lib/utils";
import { useState } from "react";

type Value = {
  id: string;
  names: string;
  entityType?: string;
};

export function RelationMultiInput({
  name,
  label,
  loadOptions,
  placeholder,
  addLabel,
  renderOption,
  optionLabel,
}: {
  name: string;
  label: string;
  loadOptions: (query: string) => Promise<Value[]>;
  placeholder?: string;
  addLabel?: string;
  renderOption?: (value: Value) => string;
  optionLabel?: (value: Value) => string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      <PillValueList name={name} />
      <AddItemControl
        name={name}
        loadOptions={loadOptions}
        placeholder={placeholder ?? `Search ${label.toLowerCase()}`}
        addLabel={addLabel ?? "Add"}
        renderOption={renderOption}
        optionLabel={optionLabel}
      />
    </div>
  );
}
function AddItemControl({
  name,
  loadOptions,
  placeholder,
  addLabel,
  renderOption,
  optionLabel,
}: {
  name: string;
  loadOptions: (query: string) => Promise<Value[]>;
  placeholder: string;
  addLabel: string;
  renderOption?: (value: Value) => string;
  optionLabel?: (value: Value) => string;
}) {
  const [value, setValue] = useState<Value | null>(null);

  const { setValue: setFormValue, watch } = useFormContext();
  const values = watch(name) as Value[];
  const onAddItem = () => {
    const valueExists = values.some((v) => v.id === value?.id);
    if (!valueExists && value) {
      setFormValue(name, [...values, value]);
    }
    setValue(null);
  };
  return (
    <div className="flex gap-2 items-start">
      <Combobox<Value>
        className="w-full max-w-xl"
        loadOptions={loadOptions}
        value={value}
        renderOption={renderOption ?? ((v) => extractFromMultiName(v.names))}
        onChange={setValue}
        placeholder={placeholder}
        optionLabel={optionLabel}
      />
      <Button type="button" onClick={onAddItem} disabled={!value}>
        {addLabel}
      </Button>
    </div>
  );
}

export function PillValueList({ name }: { name: string }) {
  const { watch, setValue } = useFormContext();
  const values = watch(name) || [];

  const removeItem = (v: Value) => {
    setValue(
      name,
      values.filter((vv: Value) => v.id !== vv.id),
    );
  };

  return (
    <div className="flex flex-wrap gap-2">
      {values.map((value: Value) => (
        <RemovablePill key={value.id} value={value} onRemove={removeItem} />
      ))}
    </div>
  );
}

function RemovablePill({ value, onRemove }: { value: Value; onRemove: (value: Value) => void }) {
  const { ref, isHovered } = useHoverState<HTMLButtonElement>();

  return (
    <span
      className={cn(
        "transition-colors px-4 py-2 text-base text-white rounded-full bg-primary flex items-center gap-2",
        isHovered && "bg-destructive",
      )}
    >
      {extractFromMultiName(value.names)}
      <button ref={ref} onClick={() => onRemove(value)} className="hover:text-gray-300">
        <XIcon size={16} />
      </button>
    </span>
  );
}
